/**
 * @fileoverview Shared enums and constants for resources and tools
 */

/**
 * System-wide Enumerations and Constants
 *
 * This module defines general-purpose enumerations used throughout the resource system. Key enums include:
 *   - ResourceType: Identifies the basic types of resources (e.g., Quiz, Analyzer, Guide, etc.).
 *   - ToolType: Enumerates the various tool types available in the system.
 *   - ToolCategory: Provides broad categorization for tool groupings.
 *
 * Note:
 *   There is some overlap with definitions in the following modules:
 *     - categories.ts: Provides a unified way to represent all resource instances (resourceinstance),
 *       which is useful for discriminated unions and type inference issues in heterogeneous collections.
 *     - platform.ts: Contains platform-specific enumerations and types that may resemble some of these enums.
 *
 * Usage:
 *   - Use ResourceType for type-checking and discriminated unions in resource logic.
 *   - Use categories.ts when a universal categorization is required.
 *   - Use platform.ts exclusively for platform-dependent type logic.
 *
 * Further architecture improvements may consider unifying these definitions; however, for now they are
 * maintained separately to address domain-specific requirements.
 */

/**
 * @fileoverview Core enums for the resource system
 * These enums define the valid values for various resource properties
 */

/**
 * All possible resource types in the system
 * 
 * @typedef {Object} ResourceType
 * @property {string} TOOL - Tool resource type
 * @property {string} GUIDE - Guide resource type
 * @property {string} TEMPLATE - Template resource type
 * @property {string} CASE_STUDY - Case study resource type
 * @property {string} EBOOK - E-book resource type
 * @property {string} GLOSSARY_TERM - Glossary term resource type
 */
export const ResourceType = {
  TOOL: 'tool',
  GUIDE: 'guide',
  TEMPLATE: 'template',
  CASE_STUDY: 'case-study',
  EBOOK: 'ebook',
  GLOSSARY_TERM: 'glossary-term'
} as const

export type ResourceType = typeof ResourceType[keyof typeof ResourceType]

/**
 * All possible tool types in the system
 * 
 * @typedef {Object} ToolType
 * @property {string} CALCULATOR - Calculator tool type
 * @property {string} ANALYZER - Analyzer tool type
 * @property {string} QUIZ - Quiz tool type
 * @property {string} GENERATOR - Generator tool type
 */
export const ToolType = {
  CALCULATOR: 'calculator',
  ANALYZER: 'analyzer',
  QUIZ: 'quiz',
  GENERATOR: 'generator'
} as const

export type ToolType = typeof ToolType[keyof typeof ToolType]

/**
 * Tool categories for organization
 * 
 * @typedef {Object} ToolCategories
 * @property {string} ANALYTICS - Analytics category
 * @property {string} OPTIMIZATION - Optimization category
 * @property {string} PLATFORM - Platform category
 * @property {string} STRATEGY - Strategy category
 * @property {string} CREATIVE - Creative category
 * @property {string} AUDIENCE - Audience category
 * @property {string} PERFORMANCE - Performance category
 * @property {string} TESTING - Testing category
 * @property {string} AUTOMATION - Automation category
 * @property {string} DATA_ANALYSIS - Data analysis category
 * @property {string} CAMPAIGN_MANAGEMENT - Campaign management category
 * @property {string} AUDIENCE_TARGETING - Audience targeting category
 * @property {string} BUDGET_PLANNING - Budget planning category
 */
export const ToolCategories = {
  ANALYTICS: 'analytics',
  OPTIMIZATION: 'optimization',
  PLATFORM: 'platform',
  STRATEGY: 'strategy',
  CREATIVE: 'creative',
  AUDIENCE: 'audience',  
  PERFORMANCE: 'performance',
  TESTING: 'testing',
  AUTOMATION: 'automation',
  DATA_ANALYSIS: 'data-analysis',
  CAMPAIGN_MANAGEMENT: 'campaign-management',
  AUDIENCE_TARGETING: 'audience-targeting',
  BUDGET_PLANNING: 'budget-planning'
} as const

export type ToolCategory = typeof ToolCategories[keyof typeof ToolCategories]

/**
 * Difficulty levels for resources
 * 
 * @typedef {Object} DifficultyLevel
 * @property {string} BEGINNER - Beginner level
 * @property {string} INTERMEDIATE - Intermediate level
 * @property {string} ADVANCED - Advanced level
 * @property {string} EXPERT - Expert level
 */
export const DifficultyLevel = {
  BEGINNER: 'beginner',
  INTERMEDIATE: 'intermediate',
  ADVANCED: 'advanced',
  EXPERT: 'expert'
} as const

export type DifficultyLevel = typeof DifficultyLevel[keyof typeof DifficultyLevel]

/**
 * Learning resource type identifiers - extends ResourceType to include external resources
 * 
 * @typedef {Object} LearningResourceTypes
 * @property {string} VIDEO - Video resource type
 * @property {string} ARTICLE - Article resource type
 * @property {string} ARTICLE_COLLECTION - Article collection resource type
 * @property {string} DOCUMENTATION - Documentation resource type
 * @property {string} TUTORIAL - Tutorial resource type
 * @property {string} COURSE - Course resource type
 * @property {string} TEMPLATE - Template resource type
 * @property {string} GUIDE - Guide resource type
 * @property {string} CASE_STUDY - Case study resource type
 * @property {string} EBOOK - E-book resource type
 * @property {string} GLOSSARY - Glossary resource type
 * @property {string} TOOL - Tool resource type
 * @property {string} QUIZ - Quiz resource type
 * @property {string} CALCULATOR - Calculator resource type
 * @property {string} GENERATOR - Generator resource type
 * @property {string} ANALYZER - Analyzer resource type
 */
export const LearningResourceTypes = {
  ...ResourceType,
  VIDEO: 'video',
  ARTICLE: 'article',
  ARTICLE_COLLECTION: 'article-collection',
  DOCUMENTATION: 'documentation',
  TUTORIAL: 'tutorial',
  COURSE: 'course',
  TEMPLATE: 'template',
  GUIDE: 'guide',
  CASE_STUDY: 'case-study',
  EBOOK: 'ebook',
  GLOSSARY: 'glossary',
  TOOL: 'tool',
  QUIZ: 'quiz',
  CALCULATOR: 'calculator',
  GENERATOR: 'generator',
  ANALYZER: 'analyzer',
} as const

export type LearningResourceType = typeof LearningResourceTypes[keyof typeof LearningResourceTypes]

/**
 * Resource category identifiers
 * 
 * @typedef {Object} ResourceCategory
 * @property {string} PLATFORM - Platform category
 * @property {string} STRATEGY - Strategy category
 * @property {string} AUTOMATION - Automation category
 * @property {string} CREATIVE - Creative category
 */
export const ResourceCategory = {
  PLATFORM: 'platform',
  STRATEGY: 'strategy',
  AUTOMATION: 'automation',
  CREATIVE: 'creative',
} as const

export type ResourceCategory = typeof ResourceCategory[keyof typeof ResourceCategory]

/**
 * Chart type definitions with visualization options
 * 
 * @typedef {Object} ChartTypes
 * @property {string} bar - Standard bar chart
 * @property {string} bar-grouped - Grouped bar chart
 * @property {string} bar-horizontal - Horizontal bar chart
 * @property {string} line - Line chart
 * @property {string} area - Area chart
 * @property {string} pie - Pie chart
 * @property {string} donut - Donut chart
 * @property {string} scatter - Scatter plot
 * @property {string} histogram - Histogram chart
 * @property {string} boxplot - Box and whisker plot
 * @property {string} density - Density plot
 * @property {string} wordCloud - Word cloud visualization
 * @property {string} stream - Stream chart
 * @property {string} bubble - Bubble chart
 * @property {string} heatmap - Heatmap visualization
 * @property {string} treemap - Treemap visualization
 * @property {string} network - Network graph
 * @property {string} chord - Chord diagram
 * @property {string} sankey - Sankey diagram
 * @property {string} radar - Radar chart
 * @property {string} funnel - Funnel chart
 * @property {string} default - Default chart type
 * @property {string} stacked - Alias for stack
 * @property {string} sparkline - Sparkline chart
 * @property {string} grouped - Alias for bar-grouped
 * @property {string} stacked-bar - Alias for stack
 * @constant {Readonly<Record<string,string>>}
 */
export const ChartTypes = {
  // Basic Charts
  bar: 'bar',
  'bar-grouped': 'bar-grouped',
  'bar-horizontal': 'bar-horizontal',
  line: 'line',
  area: 'area',
  pie: 'pie',
  donut: 'donut',
  scatter: 'scatter',
  
  // Statistical Charts
  histogram: 'histogram',
  boxplot: 'boxplot',
  density: 'density',
  
  // Specialized Charts
  wordCloud: 'wordCloud',
  stream: 'stream',
  bubble: 'bubble',
  heatmap: 'heatmap',
  treemap: 'treemap',
  funnel: 'funnel',
  
  // Relationship Charts
  network: 'network',
  chord: 'chord',
  sankey: 'sankey',
  radar: 'radar',
  
  // Variants & Aliases
  sparkline: 'sparkline',
} as const


export type ChartType = typeof ChartTypes[keyof typeof ChartTypes]


/**
 * Operation types for calculator formulas
 * 
 * @typedef {Object} OperationTypes
 * @property {string} BASIC - Basic operation
 * @property {string} COMPOSITE - Composite operation
 * @property {string} DIVIDE - Division operation
 * @property {string} MULTIPLY - Multiplication operation
 * @property {string} SUBTRACT - Subtraction operation
 * @property {string} ADD - Addition operation
 * @property {string} POWER - Power/exponent operation
 * @property {string} ROOT - Root operation
 * @property {string} AVERAGE - Average operation
 * @property {string} RATIO - Ratio operation
 * @property {string} PERCENTAGE - Percentage operation
 */
export const OperationTypes = {
  BASIC: 'basic',
  COMPOSITE: 'composite',
  DIVIDE: 'divide',
  MULTIPLY: 'multiply',
  SUBTRACT: 'subtract',
  ADD: 'add',
  POWER: 'power',
  ROOT: 'root',
  AVERAGE: 'average',
  RATIO: 'ratio',
  PERCENTAGE: 'percentage'
} as const

export type OperationType = typeof OperationTypes[keyof typeof OperationTypes]

/**
 * @fileoverview Shared enumerations and constants for the resource system.
 * Defines core type enumerations, categories, and related constants used across resources.
 * @module resources/types/shared/enums
 */

// =========================================
// Core Resource Type Enumerations
// =========================================

/**
 * Glossary category enumeration for term organization
 * 
 * @typedef {Object} GlossaryCategories
 * @property {string} METRICS - Metrics category
 * @property {string} CHARTS - Charts category
 * @property {string} PLATFORM - Platform category
 * @property {string} CREATIVE - Creative category
 * @property {string} GENERAL - General category
 */
export const GlossaryCategories = {
  METRICS: 'metrics',
  CHARTS: 'charts', 
  PLATFORM: 'platform',
  CREATIVE: 'creative',
  GENERAL: 'general'
} as const

export type GlossaryCategory = typeof GlossaryCategories[keyof typeof GlossaryCategories]

/**
 * Position type for element placement
 * 
 * @typedef {Object} PositionTypes
 * @property {string} TOP - Top position
 * @property {string} BOTTOM - Bottom position
 * @property {string} LEFT - Left position
 * @property {string} RIGHT - Right position
 * @property {string} CENTER - Center position
 */
export const PositionTypes = {
  TOP: 'top',
  BOTTOM: 'bottom',
  LEFT: 'left',
  RIGHT: 'right',
  CENTER: 'center'
} as const

export type PositionType = typeof PositionTypes[keyof typeof PositionTypes]

/**
 * Value formatter type for data display
 * 
 * @typedef {Object} ValueFormatterTypes
 * @property {string} DEFAULT - Default formatter
 * @property {string} NUMBER - Number formatter
 * @property {string} CURRENCY - Currency formatter
 * @property {string} PERCENTAGE - Percentage formatter
 * @property {string} RATIO - Ratio formatter
 * @property {string} DECIMAL - Decimal formatter
 * @property {string} INTEGER - Integer formatter
 */
export const ValueFormatterTypes = {
  DEFAULT: 'default',
  NUMBER: 'number',
  CURRENCY: 'currency',
  PERCENTAGE: 'percentage',
  RATIO: 'ratio',
  DECIMAL: 'decimal',
  INTEGER: 'integer'
} as const

export type ValueFormatterType = typeof ValueFormatterTypes[keyof typeof ValueFormatterTypes]

// =========================================
// Resource Categories & Constants
// =========================================

/**
 * Available resource categories for organizing content
 * 
 * @constant {ReadonlyArray<string>}
 */
export const RESOURCE_CATEGORIES = [
  'Performance Analytics',
  'Creative Testing',
  'Creative Optimization', 
  'Platform Optimization',
  'Strategy & Planning',
  'Data Analysis',
  'Campaign Management',
  'Audience Targeting',
  'Budget Planning',
  'Conversion Optimization',
  'A/B Testing',
  'Marketing Automation',
  'Analytics & Measurement'  
] as const


/**
 * Constant array of glossary categories with metadata
 * 
 * @typedef {Object} GlossaryCategoryItem
 * @property {GlossaryCategory} id - Unique identifier for the category
 * @property {string} label - Display name for the category
 * @property {string} description - Short description of the category
 * @property {string} longDescription - Detailed description of the category
 * @property {string} icon - Emoji icon representing the category
 * 
 * @constant {Array<GlossaryCategoryItem>}
 * @since 1.0.0
 */
export const GLOSSARY_CATEGORIES = [
    {
      id: 'metrics' as const,
      label: 'Marketing Metrics',
      /** Short label for compact UI surfaces (hub chips, nav, breadcrumbs) */
      shortLabel: 'Metrics',
      /** Plural noun used in long-form intro copy ("N marketing metrics, each with...") */
      pluralLabel: 'metrics',
      description: 'Key performance indicators and measurement metrics',
      longDescription: 'Comprehensive guide to key performance indicators, measurement metrics, and analytics terms used in digital marketing. Learn how to track, measure, and optimize your advertising campaigns with these essential marketing metrics and KPIs.',
      icon: '📊'
    },
    {
      id: 'creative' as const,
      label: 'Creative Terms',
      shortLabel: 'Creative',
      pluralLabel: 'creative terms',
      description: 'Creative strategy and design terminology',
      longDescription: 'Essential creative strategy, design terminology, and concepts for effective digital advertising. Explore key terms related to ad creative development, visual design principles, and creative optimization techniques used by successful marketers.',
      icon: '🎨'
    },
    {
      id: 'platform' as const,
      label: 'Platform-Specific',
      shortLabel: 'Platform',
      pluralLabel: 'platform-specific terms',
      description: 'Terms specific to advertising platforms',
      longDescription: 'Detailed explanations of terminology specific to major advertising platforms including Meta, Google, TikTok, and more. Master the platform-specific features, tools, and metrics that drive successful cross-platform advertising campaigns.',
      icon: '🚀'
    },
    {
      id: 'general' as const,
      label: 'General Terms',
      shortLabel: 'General',
      pluralLabel: 'general marketing terms',
      description: 'Common marketing and advertising concepts',
      longDescription: 'Fundamental marketing and advertising concepts every digital marketer should know. This comprehensive glossary covers essential terminology, strategies, and principles that form the foundation of effective digital marketing and advertising.',
      icon: '📚'
    },
    {
      id: 'charts' as const,
      label: 'Charts & Visualizations',
      shortLabel: 'Charts',
      pluralLabel: 'chart types',
      description: 'Data visualization and chart types',
      longDescription: 'Complete guide to data visualization techniques and chart types used in marketing analytics and reporting. Learn how to effectively present marketing data through various visualization methods to extract meaningful insights and communicate results.',
      icon: '📈'
  }
] as const

/**
 * Compact category label for chips and badges (e.g. "Metrics", not "metrics").
 *
 * @param {GlossaryCategory} categoryId - Glossary category slug.
 * @returns {string} Title-cased short label.
 */
export function getGlossaryCategoryShortLabel(categoryId: GlossaryCategory): string {
  const category = GLOSSARY_CATEGORIES.find((cat) => cat.id === categoryId)
  if (category) {
    return category.shortLabel
  }
  return categoryId.charAt(0).toUpperCase() + categoryId.slice(1)
}

/**
 * Mapping of singular resource types to their plural forms
 *
 * @typedef {Object.<string, string>} ResourceTypePlurals
 * @constant {Record<string, string>}
 */
export const RESOURCE_TYPE_PLURALS: Record<string, string> = {
  'calculator': 'calculators',
  'analyzer': 'analyzers',
  'quiz': 'quizzes',
  'generator': 'generators',
  'tool': 'tools',
  'guide': 'guides',
  'glossary': 'glossary',
  'template': 'templates',
  'case-study': 'case-studies',
  'ebook': 'ebooks'
}

/**
 * Human-readable display labels for resource and tool type slugs.
 *
 * Used to render type badges/chips (e.g. on related-resource cards) as
 * "Glossary Term" rather than the raw `glossary-term` slug.
 *
 * The `satisfies Record<ResourceType | ToolType, string>` constraint makes
 * this map exhaustive at compile time: adding a new {@link ResourceType} or
 * {@link ToolType} without a label here is a type error, so the badge formatter
 * can never silently fall through to the title-case fallback for a known type.
 *
 * @constant
 */
export const RESOURCE_TYPE_LABELS = {
  'tool': 'Tool',
  'guide': 'Guide',
  'template': 'Template',
  'case-study': 'Case Study',
  'ebook': 'Ebook',
  'glossary-term': 'Glossary Term',
  'calculator': 'Calculator',
  'analyzer': 'Analyzer',
  'quiz': 'Quiz',
  'generator': 'Generator'
} satisfies Record<ResourceType | ToolType, string>

/**
 * Returns a human-readable display label for a resource or tool type slug.
 *
 * Known {@link ResourceType}/{@link ToolType} values resolve via
 * {@link RESOURCE_TYPE_LABELS}. Any other slug falls back to title-casing
 * (splitting on hyphens), so an unknown `multi-word-type` still renders as
 * "Multi Word Type" rather than the raw slug. A falsy `type` (defensive guard
 * against malformed/partial data) returns an empty string instead of throwing.
 *
 * @param {string} type - A resource type or tool type slug (e.g. `glossary-term`).
 * @returns {string} The display label (e.g. "Glossary Term").
 */
export function getResourceTypeLabel(type: string): string {
  if (!type) return ''
  return (
    (RESOURCE_TYPE_LABELS as Record<string, string>)[type] ??
    type
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  )
}
